import React from 'react';
import { Chip } from '@jotpsych/ui';

import { getCoverageDisplayStatus } from '../store/hooks';
import type { CoverageDisplayStatus, PatientCoverage } from '../types';

const formatShortDate = (isoDate?: string | null): string | null => {
  if (!isoDate) return null;
  const parsed = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const CHIP_CONFIG: Record<
  CoverageDisplayStatus,
  { color: 'success' | 'warning' | 'info' | 'default' | 'error'; label: string }
> = {
  active: { color: 'success', label: 'Active' },
  pending: { color: 'warning', label: 'Pending' },
  future: { color: 'info', label: 'Upcoming' },
  expired: { color: 'default', label: 'Expired' },
  cancelled: { color: 'error', label: 'Cancelled' },
};

/**
 * Derived coverage lifecycle chip (GDN-2411). Status comes from the date
 * window vs today, not the stored status — a plan past its termination
 * date reads Expired without any stored flip; an incoming plan reads
 * "Starts <date>".
 */
export const CoverageStatusChip: React.FC<{
  coverage: Partial<PatientCoverage>;
}> = ({ coverage }) => {
  const displayStatus = getCoverageDisplayStatus(coverage);
  const config = CHIP_CONFIG[displayStatus];

  let label = config.label;
  if (displayStatus === 'future') {
    const start = formatShortDate(coverage.effective_date);
    label = start ? `Starts ${start}` : 'Upcoming';
  } else if (displayStatus === 'active' && coverage.termination_date) {
    const end = formatShortDate(coverage.termination_date);
    label = end ? `Active until ${end}` : 'Active';
  }

  return <Chip variant="soft" size="xSmall" color={config.color} label={label} />;
};

/**
 * Compact "Jan 1, 2026 – Jun 30, 2026" coverage window line for card faces
 * and history rows. Open bounds render as "Effective <date>" / "Until <date>".
 */
export const formatCoverageWindow = (
  coverage: Partial<PatientCoverage>,
): string | null => {
  const start = formatShortDate(coverage.effective_date);
  const end = formatShortDate(coverage.termination_date);
  if (start && end) return `${start} – ${end}`;
  if (start) return `Effective ${start}`;
  if (end) return `Until ${end}`;
  return null;
};
